import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Input,
  Label,
  PageContainer,
  PageIntroCard,
  PageSectionCard,
  Skeleton,
  toast,
} from '@kmgestao/ui';

import {
  useCreateDashboard,
  useDashboards,
  useDeleteDashboard,
} from '../../../features/dashboards/index.js';
import { SuperadminShell } from '../SuperadminShell.js';

/**
 * Listagem de dashboards do Superadmin. Inline form de criação
 * (nome + descrição opcional) e deletar com confirmação simples.
 * Escopo M0: global only (clinic/personal ficam pra M1 quando
 * clinicSwitcher estiver pronto).
 */
export function DashboardsListPage() {
  const { data: dashboards, isLoading, isError, error } = useDashboards();
  const create = useCreateDashboard();
  const remove = useDeleteDashboard();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const onCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    await create.mutateAsync(
      {
        name: name.trim(),
        description: description.trim() || null,
        scope: 'global',
        clinic_id: null,
        is_shared: false,
      },
      {
        onSuccess: () => {
          toast.success('Dashboard criado.');
          setName('');
          setDescription('');
        },
        onError: (err) =>
          toast.error('Falha ao criar dashboard', { description: (err as Error).message }),
      },
    );
  };

  const onDelete = async (id: string, dashboardName: string) => {
    if (
      !window.confirm(
        `Remover o dashboard "${dashboardName}"? Esta ação é reversível (soft-delete).`,
      )
    ) {
      return;
    }
    await remove.mutateAsync(id, {
      onSuccess: () => toast.success('Dashboard removido.'),
      onError: (err) => toast.error('Falha ao remover', { description: (err as Error).message }),
    });
  };

  return (
    <SuperadminShell>
      <PageContainer>
        <PageIntroCard
          eyebrow="Superadmin › Dashboards"
          title="Construtor de dashboards"
          description={
            'Crie painéis nativos que combinam KPIs, gráficos Recharts e insights ' +
            'gerados por IA. Cada dashboard pertence a um escopo (global para toda ' +
            'a plataforma, clinic para uma clínica específica ou personal).'
          }
        />

        <PageSectionCard
          title="Novo dashboard"
          description="Criação rápida — widgets adicionados no editor individual de cada painel."
        >
          <form className="grid gap-4 sm:grid-cols-2" onSubmit={onCreate} noValidate>
            <div className="space-y-2">
              <Label htmlFor="dashboard-name">Nome</Label>
              <Input
                id="dashboard-name"
                placeholder="Ex: Saúde operacional"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={120}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dashboard-desc">Descrição (opcional)</Label>
              <Input
                id="dashboard-desc"
                placeholder="O que este dashboard responde"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
              />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Criando…' : 'Criar dashboard'}
              </Button>
            </div>
          </form>
        </PageSectionCard>

        <PageSectionCard
          title="Dashboards existentes"
          description={
            dashboards?.length
              ? `${dashboards.length} painel${dashboards.length === 1 ? '' : 's'} ativo${dashboards.length === 1 ? '' : 's'}.`
              : 'Nenhum dashboard cadastrado ainda.'
          }
        >
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              <p className="font-semibold">Falha ao carregar dashboards.</p>
              <p className="mt-1 font-mono text-xs">{error?.message}</p>
            </div>
          ) : dashboards?.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Use o formulário acima para criar o primeiro.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {dashboards?.map((d) => (
                <li
                  key={d.id}
                  className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/superadmin/dashboards/${d.id}`}
                        className="text-base font-semibold text-foreground hover:underline"
                      >
                        {d.name}
                      </Link>
                      <Badge variant="secondary">{d.scope}</Badge>
                      {d.is_shared ? <Badge>Compartilhado</Badge> : null}
                      {d.is_default ? <Badge variant="outline">Padrão</Badge> : null}
                    </div>
                    {d.description ? (
                      <p className="text-sm text-muted-foreground">{d.description}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/superadmin/dashboards/${d.id}`}>Abrir</Link>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDelete(d.id, d.name)}
                      disabled={remove.isPending}
                    >
                      Remover
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </PageSectionCard>
      </PageContainer>
    </SuperadminShell>
  );
}
